import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { injectMutation, injectQueryClient } from '@ngneat/query';
import { AuthService } from '../services/auth.service';
import { AuthResponse, AccountSummary, AuthStatus, TENANT_SWITCH_ROLES, TEMPLATE_MANAGEMENT_ROLES, SYSTEM_ALERTS_VIEW_ROLES, Role } from '../models/auth.models';
import { Router } from '@angular/router';
import { SettingsService } from '../../../core/services/settings.service';
import { SETTINGS_KEYS } from '../../../core/constants/settings-keys';

@Injectable({ providedIn: 'root' })
export class SessionState {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly queryClient = injectQueryClient();
  private readonly mutation = injectMutation();
  private readonly settings = inject(SettingsService);

  // Estado reactivo mediante Signals con carga inicial desde SettingsService
  readonly account = signal<AccountSummary | null>(this.settings.get<AccountSummary>(SETTINGS_KEYS.AUTH_USER));
  readonly error = signal<string | null>(null);
  private sessionExpirationHandled = false;

  // Derivados para UI y Guards
  readonly isAuthenticated = computed(() => !!this.account());

  /** Rol activo del usuario autenticado. Null si no hay sesión. */
  readonly currentRole = computed<Role | null>(() => this.account()?.role ?? null);

  /**
   * True si el rol puede cambiar de empresa (tenant) en el selector.
   * TODO-DEBT(PERMISSIONS): Reemplazar por permissions cuando el backend los envíe.
   */
  readonly canSwitchTenant = computed<boolean>(() => {
    const role = this.currentRole();
    if (!role) return false;
    return TENANT_SWITCH_ROLES.includes(role.name);
  });

  /**
   * True si el rol puede gestionar templates (sincronizar, aprobar, historial, editar asunto, ver badges pendientes).
   * TODO-DEBT(PERMISSIONS): Reemplazar por permissions cuando el backend los envíe.
   */
  readonly canManageTemplates = computed<boolean>(() => {
    const role = this.currentRole();
    if (!role) return false;
    return TEMPLATE_MANAGEMENT_ROLES.includes(role.name);
  });

  /**
   * True si el rol puede ver el panel de "Alertas del sistema" del dashboard de Performance de Envíos.
   * TODO-DEBT(PERMISSIONS): Reemplazar por permissions cuando el backend los envíe.
   */
  readonly canViewSystemAlerts = computed<boolean>(() => {
    const role = this.currentRole();
    if (!role) return false;
    return SYSTEM_ALERTS_VIEW_ROLES.includes(role.name);
  });

  // Mutación de Login (Patrón Horus)
  readonly loginMutation = this.mutation({
    mutationFn: async () => {
      const firebaseToken = await this.authService.signInWithMicrosoft();
      const response = await this.authService.login(firebaseToken);
      return response;
    },
    onSuccess: (response: AuthResponse) => {
      this.handleAuthSuccess(response);
    },
    onError: (error: unknown) => {
      this.handleAuthError(error);
    }
  });

  // Mutación de Logout (Patrón Horus)
  readonly logoutMutation = this.mutation({
    mutationFn: async () => await this.authService.logout(),
    onSuccess: () => {
      this.clearAuthData();
      this.router.navigate(['/auth/login']);
    },
    onError: (error: unknown) => {
      console.error('[SessionState] Logout error:', error);
      this.clearAuthData();
      this.router.navigate(['/auth/login']);
    }
  });

  constructor() {
    this.loadAuthData();
  }

  // Estado semántico de la autenticación
  // NOTA: Se evita usar result() dentro de un computed para prevenir el error NG0602
  // El estado de carga y error se consume directamente de las mutaciones en los componentes
  readonly status = computed<AuthStatus>(() => {
    if (this.isAuthenticated()) return 'authenticated';
    return 'unauthenticated';
  });

  login() {
    this.error.set(null);
    this.loginMutation.reset();
    this.loginMutation.mutate({});
  }

  logout() {
    this.loginMutation.reset();
    this.logoutMutation.reset();
    this.logoutMutation.mutate({});
  }

  /**
   * Limpia inmediatamente la sesión local cuando el backend responde 401 en un endpoint protegido.
   * No ejecuta el logout remoto para evitar recursión/cascadas cuando la cookie o token ya expiró.
   */
  expireLocalSession(): void {
    if (this.sessionExpirationHandled) return;

    this.sessionExpirationHandled = true;
    this.loginMutation.reset();
    this.logoutMutation.reset();
    this.clearAuthData();
    this.router.navigate(['/auth/login']);
  }

  private handleAuthSuccess(response: AuthResponse) {
    if (!response?.account) {
      this.error.set('Tu cuenta no tiene acceso a esta aplicación.');
      this.loginMutation.reset();
      return;
    }

    this.settings.set(SETTINGS_KEYS.AUTH_USER, response.account);
    this.account.set(response.account);
    this.error.set(null);
    this.sessionExpirationHandled = false;
    this.router.navigate(['/comunicaciones']);
  }

  private handleAuthError(error: unknown): void {
    // Errores de Firebase: el usuario cerró el popup — no mostrar mensaje
    if (
      typeof error === 'object' &&
      error !== null &&
      'code' in error &&
      (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-by-user')
    ) {
      return;
    }

    console.error('[SessionState] Login error:', error);

    // Errores HTTP: usar el mensaje del backend si está disponible
    if (error instanceof HttpErrorResponse) {
      const backendMessage = error.error?.message;
      this.error.set(backendMessage ?? 'Error al iniciar sesión. Intenta nuevamente.');
      return;
    }

    // Otros errores (red, Firebase desconocido, etc.)
    const message =
      error instanceof Error ? error.message : 'Error al iniciar sesión con Microsoft';
    this.error.set(message);
  }

  private loadAuthData(): void {
    const account = this.settings.get<AccountSummary>(SETTINGS_KEYS.AUTH_USER);
    if (!account) return;

    this.account.set(account);

    if (this.router.url.startsWith("/auth/login")) {
      this.router.navigate(['/comunicaciones']);
    }
  }

  private clearAuthData() {
    this.settings.remove(SETTINGS_KEYS.AUTH_USER);
    this.account.set(null);
    this.error.set(null);
    this.queryClient.clear();
  }

  resetAllMutations(): void {
    this.loginMutation.reset();
    this.logoutMutation.reset();
  }
}
